import type {
  AddCardBillingMethodFn,
  AddCouponFn,
  ChangeDefaultBillingMethodFn,
  FetchBillingMethodsFn,
  FetchBillingProfileFn,
  FetchBillingTransactionsFn,
  FetchCouponByCodeFn,
  FetchCouponFn,
  FetchMemberCouponsFn,
  BillingMethodsFn,
  BillingTransactionsFn,
  CanManageBillingFn,
  GetStripePublishableKeyFn,
  ListCouponsFn,
  NewPaymentMethodFn,
  SelectPaymentMethodFn,
} from '@/features/billing'
import type {
  ArchiveDocumentFn,
  ConfirmDocumentFn,
  DocumentCreationModalViewModelFactory,
  DocumentPickerViewModelFactory,
  DocumentRequestsViewModelFactory,
  DownloadDocumentFn,
  DownloadDocumentsFn,
  FetchDocumentTypesFn,
  JobDocumentsFn,
  PrimeDocumentFn,
  PrimeDocumentTypeByShortIdFn,
  SearchDocumentsFn,
  ShowDocumentDetailsFn,
  UploadBarViewModelFactory,
  JobDocumentListViewModelFactory,
} from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import type {
  EnsureTermsAcceptedFn,
  FetchMemberFn,
  FetchMembersByPublicIdsFn,
  FetchTeamMemberFn,
  FetchUserDataFn,
  ListUserTeamMembersFn,
  ListUserTeamMembersForWorkspaceFn,
  FetchWorkspaceFn,
  ListWorkspaceMembershipsFn,
  FilterTeamMembersFn,
  ListTeamMembersFn,
  ListUserTeamsFn,
  GetCurrentMemberFn,
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamFn,
  GetTeamMemberFn,
  GetTeamMembersForWorkspaceMemberFn,
  GetUserDataFn,
  GetWorkspaceMembersFn,
  GetWorkspaceSelectionFn,
  PrimeMemberByPublicIdFn,
  PrimeMemberFn,
  PrimeTeamFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
  SearchMembersFn,
  ListTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import type {
  ListInventoryItemsForProviderFn,
  ListInventoryItemsFn,
} from '@/features/inventory'
import { type JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import type { UserJobsTabViewModel } from '@/features/jobs/list/UserJobsTabViewModel'
import type { QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  ConversationViewModelFactory,
  FetchHumanMessagesTotalCountFn,
  ConversationFn,
  MessageBankDialogViewModelFactory,
  PrimeConversationFn,
  PrimeRoomFn,
  RoomViewModelFactory,
} from '@/features/messaging'
import type { Message } from '@/features/messaging/Message'
import type { RoomEvent } from '@/features/messaging/rooms/room-event'
import type {
  DesktopNotificationState,
  SetDesktopNotificationsEnabledFn,
} from '@/features/notifications'
import type {
  HandleOfferResponseFn,
  ListQualifiedProvidersFn,
  ProbeCanViewOpportunitiesFn,
} from '@/features/opportunities'
import type {
  FetchRelationManagerFn,
  GetRelationManagerFn,
} from '@/features/relation-manager'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { BillingProfile } from '@/features/billing/profile/BillingProfile'
import type { DocumentType } from '@/features/documents/DocumentType'
import type { GetAddToCalendarDelegate } from '@/features/shell/platform/types'
import type { NavigateFn } from '@/RootStore'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import type { KeyPress } from '@/utils/keyboard-util'
import type { Id } from '@/infra/id'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import { toStream } from '@/utils/mobx-util'
import type { SubmitOptions } from '@/features/jobs/drafting/DraftJobStore'
import type { Job, JobAnswer } from './Job'
import type { SearchableJob } from '@/features/jobs/list/SearchableJob'
import type { TimeRange } from '@/infra/time-range'
import type { SearchJobsQuery } from '@/features/jobs/list/SearchableJobStore'
import type { OwnerScopedInput } from '@/features/jobs/drafting/PrefilledDraftInput'
import type { JobCheckoutViewModel } from '@/features/jobs/drafting/checkout/JobCheckoutViewModel'
import type { Consultation } from '@/features/jobs/consultations/Consultation'
import type {
  JobTotalStatusAggregations,
  JobTotals,
} from '@/features/jobs/dashboard/job-stats/job-totals'
import type { ProOpenJobCount } from '@/features/jobs/dashboard/job-stats/pro-open-job-count'
import type { FetchJobTotalStatusAggregationsOptions } from '@/features/jobs/dashboard/job-stats/JobStatsStore'
import type {
  LegendVersionId,
  PublishedLegend,
} from '@/features/jobs/legends/PublishedLegend'
import type { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import type { CursorPaged } from '@/infra/paging'
import type { Observable } from 'rxjs'

/**
 * Streams a job from the local cache.
 */
export type JobFn = (id: Id) => Observable<Job | undefined>

/**
 * Options for fetching a job.
 */
export interface FetchJobOptions {
  /**
   * Reloads the job if already cached.
   */
  force?: boolean
}

/**
 * Fetches a job.
 */
export type FetchJobFn = (
  id: Id,
  options?: FetchJobOptions,
  signal?: AbortSignal,
) => Promise<Job>

/**
 * Props used to prime a job in the store before it is fully loaded.
 */
export interface PrimeJobProps {
  id: Id
  shortId?: string
}

/**
 * Primes a job.
 */
export type PrimeJobFn = (props: PrimeJobProps) => Job

/**
 * Fetches jobs by ID.
 */
export type FetchJobsFn = (ids: Id[], signal?: AbortSignal) => Promise<Job[]>

/**
 * Searches jobs.
 */
export type SearchJobsFn = (
  query: SearchJobsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<SearchableJob>>

/**
 * Fetches the job totals of a user.
 */
export type FetchJobTotalsFn = (
  userPublicId: Id,
  signal?: AbortSignal,
) => Promise<JobTotals>

/**
 * Fetches the job status totals of a user in a workspace.
 */
export type FetchJobTotalStatusAggregationsFn = (
  options: FetchJobTotalStatusAggregationsOptions,
  signal?: AbortSignal,
) => Promise<JobTotalStatusAggregations>

/**
 * Submits a draft job on behalf of its client.
 */
export type SubmitJobRequestFn = (options: SubmitOptions) => Promise<Job>

/**
 * Applies a coupon to a draft job by its code, as a call center agent.
 */
export type ApplyJobCouponByCodeFn = (
  jobId: Id,
  couponCode: string,
) => Promise<void>

/**
 * Removes the coupon from a draft job, as a call center agent.
 */
export type RemoveJobCouponByCodeFn = (jobId: Id) => Promise<void>

/**
 * Streams a published legend version from the local cache.
 */
export type PublishedLegendFn = (
  legendVersionId: LegendVersionId,
) => Observable<PublishedLegend | undefined>

/**
 * Creates a view model for a draft job's checkout summary.
 */
export type JobCheckoutViewModelFactory = (jobId: Id) => JobCheckoutViewModel

/**
 * Values to prefill a new draft with.
 */
export interface PrefilledDraftProps {
  legendId?: string
  userPublicId?: string
  teamId?: string | null
  answers?: JobAnswer[]
  ownerScopedInput?: OwnerScopedInput
}

/**
 * Opens the job creation dialog.
 */
export type PromptCreateJobFn = (prefill?: PrefilledDraftProps) => Promise<void>

/**
 * Outcome of picking up a job.
 */
export type PickupResult = 'Success' | 'Vanished' | 'MaxConcurrentJobs'

/**
 * Streams a job's progression from the local cache.
 */
export type JobProgressionFn = (
  jobId: Id,
) => Observable<JobProgression | undefined>

/**
 * Picks up an opportunity's job.
 */
export type PickupOpportunityFn = (opportunityId: Id) => Promise<PickupResult>

/**
 * Accepts an offer for a job.
 */
export type AcceptOfferFn = (jobId: Id) => Promise<PickupResult>

/**
 * Fetches the open job counts of a pro.
 */
export type FetchProOpenJobCountsFn = (
  userPublicId: Id,
  signal?: AbortSignal,
) => Promise<ProOpenJobCount[]>

/**
 * Fetches the consultations the current user has booked.
 */
export type FetchBookedConsultationsFn = () => Promise<Consultation[]>

/**
 * Picks up a consultation job, optionally booking a time.
 */
export type PickupConsultationJobFn = (
  id: Id,
  bookedTime?: TimeRange,
) => Promise<PickupResult>

/**
 * Creates a view model for drafting a new job.
 */
export type JobCreationRootViewModelFactory = () => JobCreationRootViewModel

/**
 * The user whose jobs a user details page lists, and the workspaces its
 * workspace filter offers.
 */
export interface UserJobsTabViewModelOptions {
  userPublicId: () => Id
  workspaces: () => Workspace[]
}

/**
 * Creates a view model for the jobs tab of a user details page.
 */
export type UserJobsTabViewModelFactory = (
  options: UserJobsTabViewModelOptions,
) => UserJobsTabViewModel

/**
 * Creates the answer editors for questions that are not asked on a job, like
 * a legend's preview. `Documents` questions need a job, so they are not
 * supported.
 */
export type QuestionListViewModelFactory = () => QuestionListViewModel

/**
 * Options for {@link JobsFeature}.
 */
export interface JobsFeatureOptions {
  token$: Observable<string>
  pageActivity$: Observable<PageActivityDescriptor>
  userActive$: Observable<boolean>
  navigate: NavigateFn
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  renameShortcut$: Observable<KeyPress>
  getAddToCalendarDelegate: GetAddToCalendarDelegate
  currentMember$: Observable<Member>
  getCurrentUser: GetCurrentUserFn
  getCurrentMember: GetCurrentMemberFn
  primeUser: PrimeUserFn
  primeWorkspace: PrimeWorkspaceFn
  fetchWorkspace: FetchWorkspaceFn
  getWorkspaceSelection: GetWorkspaceSelectionFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  listWorkspaceMemberships: ListWorkspaceMembershipsFn
  primeMember: PrimeMemberFn
  primeMemberByPublicId: PrimeMemberByPublicIdFn
  fetchMember: FetchMemberFn
  fetchMembersByPublicIds: FetchMembersByPublicIdsFn
  searchMembers: SearchMembersFn
  getWorkspaceMembers: GetWorkspaceMembersFn
  primeTeam: PrimeTeamFn
  getTeam: GetTeamFn
  listUserTeams: ListUserTeamsFn
  getTeamMember: GetTeamMemberFn
  filterTeamMembers: FilterTeamMembersFn
  getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn
  fetchTeamMember: FetchTeamMemberFn
  listUserTeamMembers: ListUserTeamMembersFn
  listUserTeamMembersForWorkspace: ListUserTeamMembersForWorkspaceFn
  listTeamMembers: ListTeamMembersFn
  listTeamMembersForWorkspaceMember: ListTeamMembersForWorkspaceMemberFn
  getUserData: GetUserDataFn
  fetchUserData: FetchUserDataFn
  ensureTermsAccepted: EnsureTermsAcceptedFn
  primeConversation: PromiseLike<PrimeConversationFn>
  primeRoom: PromiseLike<PrimeRoomFn>
  newMessages$: Observable<Message>
  roomEvents$: Observable<RoomEvent>
  unreadJobIds$: Observable<Id[]>
  conversation: ConversationFn
  fetchHumanMessagesTotalCount: FetchHumanMessagesTotalCountFn
  createConversationViewModel: PromiseLike<ConversationViewModelFactory>
  createRoomViewModel: PromiseLike<RoomViewModelFactory>
  createMessageBankDialogViewModel: PromiseLike<MessageBankDialogViewModelFactory>
  desktopNotificationState$: Observable<DesktopNotificationState>
  setDesktopNotificationsEnabled: SetDesktopNotificationsEnabledFn
  primeDocument: PromiseLike<PrimeDocumentFn>
  searchDocuments: SearchDocumentsFn
  archiveDocument: ArchiveDocumentFn
  downloadDocument: DownloadDocumentFn
  downloadDocuments: DownloadDocumentsFn
  confirmDocument: ConfirmDocumentFn
  showDocumentDetails: ShowDocumentDetailsFn
  jobDocuments: JobDocumentsFn
  documentManualVerificationRequired$: Observable<Document>
  primeDocumentTypeByShortId: PromiseLike<PrimeDocumentTypeByShortIdFn>
  documentTypes$: Observable<DocumentType[]>
  fetchDocumentTypes: FetchDocumentTypesFn
  createDocumentPickerViewModel: PromiseLike<DocumentPickerViewModelFactory>
  createUploadBarViewModel: PromiseLike<UploadBarViewModelFactory>
  createDocumentCreationModalViewModel: PromiseLike<DocumentCreationModalViewModelFactory>
  createJobDocumentListViewModel: PromiseLike<JobDocumentListViewModelFactory>
  createDocumentRequestsViewModel: PromiseLike<DocumentRequestsViewModelFactory>
  fetchBillingTransactions: FetchBillingTransactionsFn
  billingTransactions: BillingTransactionsFn
  fetchBillingProfile: FetchBillingProfileFn
  canManageBilling: CanManageBillingFn
  billingProfiles$: Observable<BillingProfile[]>
  fetchBillingMethods: FetchBillingMethodsFn
  billingMethods: BillingMethodsFn
  changeDefaultBillingMethod: ChangeDefaultBillingMethodFn
  promptSelectPaymentMethod: SelectPaymentMethodFn
  promptNewPaymentMethod: NewPaymentMethodFn
  getStripePublishableKey: GetStripePublishableKeyFn
  addCardBillingMethod: AddCardBillingMethodFn
  addCoupon: AddCouponFn
  fetchCoupon: FetchCouponFn
  fetchCouponByCode: FetchCouponByCodeFn
  listCoupons: ListCouponsFn
  fetchMemberCoupons: FetchMemberCouponsFn
  searchServiceItems: ListInventoryItemsFn
  searchInventoryItemsForProvider: ListInventoryItemsForProviderFn
  probeCanViewOpportunities: ProbeCanViewOpportunitiesFn
  listQualifiedProviders: ListQualifiedProvidersFn
  handleOfferResponse: HandleOfferResponseFn
  fetchRelationManager: FetchRelationManagerFn
  getRelationManager: GetRelationManagerFn
}

/**
 * Features relating to jobs.
 */
export type JobsFeature = ReturnType<typeof JobsFeature>

export function JobsFeature(options: JobsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalJobsFeature(options),
  )

  const job: JobFn = (id) =>
    internal.deferUntilLoaded((m) =>
      toStream(() => m.jobStore.getJob(id), true),
    )

  return {
    // Internal
    internal,

    // Public
    job,
    fetchJob: internal.wrap((m) => m.jobStore.fetchJob) satisfies FetchJobFn,
    primeJob: internal.lazy<PrimeJobFn>((m) => m.jobStore.primeJob),
    fetchJobs: internal.wrap((m) => m.jobStore.fetchJobs) satisfies FetchJobsFn,
    searchJobs: internal.wrap(
      (m) => m.searchableJobStore.search,
    ) satisfies SearchJobsFn,
    fetchJobTotals: internal.wrap(
      (m) => m.jobStatsStore.fetchJobTotals,
    ) satisfies FetchJobTotalsFn,
    fetchJobTotalStatusAggregations: internal.wrap(
      (m) => m.jobStatsStore.fetchJobTotalStatusAggregations,
    ) satisfies FetchJobTotalStatusAggregationsFn,
    submitJobRequest: internal.wrap((m) => async (options: SubmitOptions) => {
      const submissionId = await m.draftJobStore.submit(options)
      return m.draftJobStore.waitForSubmissionOutcome({ submissionId })
    }) satisfies SubmitJobRequestFn,
    applyJobCouponByCode: internal.wrap(
      (m) => (jobId: Id, couponCode: string) =>
        m.jobStore.callCenterApplyCouponByCode(jobId, couponCode),
    ) satisfies ApplyJobCouponByCodeFn,
    removeJobCouponByCode: internal.wrap(
      (m) => (jobId: Id) => m.jobStore.callCenterRemoveCouponByCode(jobId),
    ) satisfies RemoveJobCouponByCodeFn,
    publishedLegend: ((legendVersionId) =>
      internal.deferUntilLoaded((m) =>
        toStream(() => m.publishedLegendStore.get(legendVersionId), true),
      )) satisfies PublishedLegendFn,
    createJobCheckoutViewModel: internal.lazy<JobCheckoutViewModelFactory>(
      (m) => m.createJobCheckoutViewModel,
    ),
    promptCreateJob: internal.wrap(
      (m) => m.promptCreateJob,
    ) satisfies PromptCreateJobFn,
    jobProgression: ((jobId) =>
      internal.deferUntilLoaded((m) =>
        toStream(() => m.progressionStore.progressionForJob(jobId), true),
      )) satisfies JobProgressionFn,
    pickupOpportunity: internal.wrap(
      (m) => m.jobStore.pickup,
    ) satisfies PickupOpportunityFn,
    acceptOffer: internal.wrap(
      (m) => m.jobStore.pickup,
    ) satisfies AcceptOfferFn,
    fetchProOpenJobCounts: internal.wrap(
      (m) => m.jobStatsStore.fetchProOpenJobCounts,
    ) satisfies FetchProOpenJobCountsFn,
    fetchBookedConsultations: internal.wrap(
      (m) => m.consultationStore.fetchBookedConsultations,
    ) satisfies FetchBookedConsultationsFn,
    pickupConsultationJob: internal.wrap(
      (m) => m.consultationStore.pickupConsultationJob,
    ) satisfies PickupConsultationJobFn,
    jobEvents$: internal.defer((m) => m.jobStore.jobEvents$),
    systemNotifications$: internal.defer((m) => m.systemNotifications$),
    workRealtime$: internal.defer((m) => m.workRealtime$),
    createJobCreationRootViewModel:
      internal.lazy<JobCreationRootViewModelFactory>(
        (m) => m.createJobCreationRootViewModel,
      ),
    createUserJobsTabViewModel: internal.lazy<UserJobsTabViewModelFactory>(
      (m) => m.createUserJobsTabViewModel,
    ),
    createQuestionListViewModel: internal.lazy<QuestionListViewModelFactory>(
      (m) => m.createQuestionListViewModel,
    ),
  }
}
